import React from 'react'
import { Flex, Box, Grid } from '@chatterbug/aaron'
import check from './assets/check.svg'
import { t } from 'src/lib/i18n-react'
import SingleStudyGoalSkeleton from 'src/ui/StudyGoals/subcomponents/SingleStudyGoal/SingleStudyGoalSkeleton'
import SingleStudyGoalProgress from 'src/ui/StudyGoals/subcomponents/SingleStudyGoalProgress/SingleStudyGoalProgress'

const circleStyles = {
  width: 32,
  height: 32,
  lineHeight: 0,
  borderRadius: '100%',
  justifySelf: 'center',
  justifyItems: 'center',
  alignItems: 'center',
  alignContent: 'center',
  justifyContent: 'center',
  color: 'white100',
}

const backgroundStyles = {
  backgroundImage: `url(${check})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
}

export const withOverflow = (
  completed: number,
  required: number,
  limit: number
) => required > limit || (completed > required && completed > limit)

export const completedLessons = (completed: number, limit: number) =>
  completed < limit ? completed : limit - 1

export const remainingLessons = (
  completed: number,
  required: number,
  limit: number
) => {
  let count
  const overflowing = withOverflow(completed, required, limit)
  if (overflowing && completed < limit) {
    count = limit - 1 - completed
  }
  if (!overflowing && completed < limit) {
    count = required - completed
  }
  if (completed === limit) {
    count = 0
  }
  if (completed >= required) {
    count = 0
  }

  return count >= 0 ? count : 0
}

export const remainingLessonCount = (
  completed: number,
  required: number,
  limit: number
) => {
  if (!withOverflow(completed, required, limit)) {
    return null
  }
  if (completed > required) {
    return completed - (limit - 1)
  } else {
    return required - (limit - 1)
  }
}

export type Props = {
  numRequiredLessons: number
  numCompletedLessons: number
  isStacked?: boolean
}

export type ContainerProps = {
  completed: number
  required: number
  max: number
}

export const Container: React.FC<ContainerProps> = ({
  max,
  completed,
  required,
}) => {
  const overflowCount = remainingLessonCount(completed, required, max)
  const numCompleted = completedLessons(completed, max)
  const numRemaining = remainingLessons(completed, required, max)
  const isFinished = completed >= required

  return (
    <Grid
      gridGap={{ _: '0,5x', tablet: '1x' }}
      justifyContent="start"
      columns={`repeat(${max}, auto)`}
    >
      {[...Array(numCompleted)].map((e, i) => (
        <Box
          data-testid="completed"
          key={i}
          {...circleStyles}
          {...backgroundStyles}
          bg="green36"
        />
      ))}
      {[...Array(numRemaining)].map((e, i) => (
        <Box
          data-testid="remaining"
          key={i}
          {...circleStyles}
          {...backgroundStyles}
          bg="gray88"
        />
      ))}
      {overflowCount && (
        <Flex
          data-testid="overflowCount"
          {...circleStyles}
          bg={isFinished ? 'green36' : 'gray80'}
        >
          {`+${overflowCount}`}
        </Flex>
      )}
    </Grid>
  )
}

const LiveLessonGoal: React.FC<Props> = ({
  numRequiredLessons,
  numCompletedLessons,
  isStacked,
}) => {
  const TABLET_MAX_ITEMS = 7
  const LAPTOP_MAX_ITEMS = 10
  const MOBILE_MAX_ITEMS = isStacked ? 7 : 3

  return (
    <SingleStudyGoalSkeleton
      title={t('Live Lessons')}
      numericProgressSection={
        <SingleStudyGoalProgress
          unit="lessons"
          completed={numCompletedLessons}
          goal={numRequiredLessons}
        />
      }
      progressBarSection={
        <>
          <Box
            display={{
              _: 'none',
              tablet: 'none',
              laptop: 'block',
            }}
            data-testid="containerLaptop"
          >
            <Container
              max={LAPTOP_MAX_ITEMS}
              completed={numCompletedLessons}
              required={numRequiredLessons}
            />
          </Box>
          <Box
            display={{
              _: 'none',
              tablet: 'block',
              laptop: 'none',
            }}
            data-testid="containerTablet"
          >
            <Container
              max={TABLET_MAX_ITEMS}
              completed={numCompletedLessons}
              required={numRequiredLessons}
            />
          </Box>
          <Box
            display={{
              _: 'block',
              tablet: 'none',
              laptop: 'none',
            }}
            data-testid="containerMobile"
          >
            <Container
              max={MOBILE_MAX_ITEMS}
              completed={numCompletedLessons}
              required={numRequiredLessons}
            />
          </Box>
        </>
      }
    />
  )
}

export default LiveLessonGoal
